import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import { useAuth } from "../../../../../../utils/Auth";
import { useClickOutside } from '../../../../../../utils/hooks/useClickOutside';
import { useFileUpload } from '../../../../../../utils/hooks/useFileUpload';
import FileInput from '../../../../../FileInput';
import FilesList from '../../../../../FilesList';
import TrackerCarousel from '../../../../../TrackerComponents/TrackerCarousel';
import styles from './FieldTypes.module.css';

// helper: turn hex color into light alpha background
const hexToRgba = (hex, alpha = 0.12) => {
    const safeAlpha = Number.isFinite(alpha) ? Math.min(Math.max(alpha, 0), 1) : 0.12;
    let c = (hex || '').replace('#', '').trim();
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    if (c.length !== 6) return `rgba(96, 124, 167, ${safeAlpha})`;
    const num = parseInt(c, 16);
    if (Number.isNaN(num)) return `rgba(96, 124, 167, ${safeAlpha})`;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

// Field Name (Column/Question Titles)
export const FieldName = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();
    const fieldTitle = useMemo(() => qrTypeParent === 'tracker' ? 'Field' : 'Question', [qrTypeParent]);

    const handleChange = (e) => {
        const value = e.target.value;

        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'name', value: value }})
    };

    return (
        <div className={styles.inputGroup}>
            <motion.input
                key={field.id}
                type='text'
                className={styles.fieldName}
                value={field.name}
                onChange={handleChange}
                placeholder={`${fieldTitle} Name`}
                required
                disabled={!edit || field.archived}
                transition={transition}
                variants={variants}
                initial={false}
                animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                style={{ pointerEvents: (edit?.id === field?.id && !field.archived) ? 'initial' : 'none' }}
            />
        </div>
    );
};

// single select
export const SingleSelect = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();
    const [ option, setOption ] = useState('');
    const [ checked, setChecked ] = useState('');

    const handleAddOption = () => {
        if (option) {
            dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: [...(field.options ?? []), option]}})
        }
        setOption('');
    };

    const handleRemoveOption = (optionToRemove) => {
        const newOptions = field.options.filter(option => option !== optionToRemove);
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
    };

    const handleEditOption = (e, optionIndex) => {
        // find and update option
        const newValue = e.target.value;
        
        if (optionIndex >= 0 && optionIndex < field.options.length) {
            const newOptions = [...field.options];
            newOptions[optionIndex] = newValue;
            
            dataDispatch({type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
        }
    };

    return (
        <>
        {field?.options?.length > 0 && field.options.map((o,i) => (
            <motion.div
                key={`option-${i}`}
                className={styles.optionWrap}
                transition={transition}
                variants={variants}
                initial='hide'
                animate='show'
                exit='hide'
                style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
            >
                <motion.label
                    className={styles.radio}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={checked === `option-${i}` ? 'checked' : 'notChecked'}
                >
                    <input type='radio' data-id={`option-${i}`} checked={checked === `option-${i}` || false} onChange={(e) => setChecked(e.target.dataset.id)} />
                    <AnimatePresence initial={false}>
                        {checked === `option-${i}` && (
                            <motion.i
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                                className='fas fa-circle'
                            />
                        )}
                    </AnimatePresence>
                </motion.label>
                <motion.input 
                    type='text' 
                    className={styles.radioInput}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={(edit?.id === field?.id) ? 'optionEdit' : 'optionNormal'}
                    value={o} 
                    maxLength='50'
                    onChange={(e) => handleEditOption(e,i)}
                />
                <AnimatePresence initial={false}>
                    {(edit?.id === field?.id) && (
                        <motion.button 
                            className={styles.optionBtn} 
                            onClick={() => handleRemoveOption(o)}
                            transition={transition}
                            variants={variants}
                            initial='hide'
                            animate='show'
                            exit='hide'
                        >
                            <i className='far fa-trash-can' />
                        </motion.button>
                    )}
                </AnimatePresence>
            </motion.div>
        ))}
        <div className={styles.optionWrap} style={{marginTop: `${field?.options?.length > 0 ? '.25rem' : '0'}`}}>
            <AnimatePresence initial={false}>
                {(field?.options?.length < 1 || (edit?.id === field?.id)) && (
                    <motion.label 
                        key='label'
                        className={styles.radio} 
                        style={{border: '1px solid #ccc', backgroundColor: '#fbfbfb'}}
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    />
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {(edit?.id === field?.id) && (
                    <motion.div
                        key='text'
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    >
                        <motion.input 
                            type='text' 
                            maxLength='50' 
                            value={option} 
                            onChange={e => setOption(e.target.value)} 
                            placeholder='Add Option' 
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={(edit?.id === field?.id) ? 'titleEdit' : 'titleNormal'}
                        />
                        <button className={styles.optionBtn} onClick={handleAddOption} disabled={!option}><i className='far fa-plus' /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        </>
    );
};

// checklist
export const CheckList = ({field, edit, qrTypeParent}) => {
    
    const { dataDispatch } = useAuth();
    const [ option, setOption ] = useState('');
    const [ checked, setChecked ] = useState([]);

    const handleAddOption = () => {
        if (option) {
            dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: [...(field.options ?? []), option]}})
        }
        setOption('');
    };

    const handleRemoveOption = (optionToRemove) => {
        const newOptions = field.options.filter(option => option !== optionToRemove);
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
    };

    const handleEditOption = (e, optionIndex) => {
        // find and update option
        const newValue = e.target.value;
        
        if (optionIndex >= 0 && optionIndex < field.options.length) {
            const newOptions = [...field.options];
            newOptions[optionIndex] = newValue;
            
            dataDispatch({type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
        }
    };

    const handleCheckboxChange = (e) => {
        const optionId = e.target.dataset.id;
        const isChecked = e.target.checked;
        
        if (isChecked) {
            if (!checked.includes(optionId)) {
                setChecked([...checked, optionId]);
            }
        } else {
            setChecked(checked.filter(id => id !== optionId));
        }
    };
    
    const isOptionChecked = (optionId) => checked.includes(optionId);

    return (
        <>
        {field?.options?.length > 0 && field.options.map((o,i) => (
            <motion.div
                key={`option-${i}`}
                className={styles.optionWrap}
                transition={transition}
                variants={variants}
                initial='hide'
                animate='show'
                exit='hide'
                style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
            >
                <motion.label
                    className={styles.checkboxInput}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={isOptionChecked(`option-${i}`) ? 'checked' : 'notChecked'}
                >
                    <input type='checkbox' data-id={`option-${i}`} checked={isOptionChecked(`option-${i}`)} onChange={handleCheckboxChange} />
                    <AnimatePresence initial={false}>
                        {isOptionChecked(`option-${i}`) && (
                            <motion.i
                                transition={transition}
                                variants={variants}
                                initial='hide'
                                animate='show'
                                exit='hide'
                                className='fas fa-check'
                            />
                        )}
                    </AnimatePresence>
                </motion.label>
                <motion.input 
                    type='text' 
                    className={styles.checkboxListInput}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={(edit?.id === field?.id) ? 'optionEdit' : 'optionNormal'}
                    value={o} 
                    maxLength='50'
                    onChange={(e) => handleEditOption(e,i)}
                />
                <AnimatePresence initial={false}>
                    {(edit?.id === field?.id) && (
                        <motion.button 
                            className={styles.optionBtn} 
                            onClick={() => handleRemoveOption(o)}
                            transition={transition}
                            variants={variants}
                            initial='hide'
                            animate='show'
                            exit='hide'
                        >
                            <i className='far fa-trash-can' />
                        </motion.button>
                    )}
                </AnimatePresence>
            </motion.div>
        ))}
        <div className={styles.optionWrap} style={{marginTop: `${field?.options?.length > 0 ? '.25rem' : '0'}`}}>
            <AnimatePresence initial={false}>
                {(field?.options?.length < 1 || (edit?.id === field?.id)) && (
                    <motion.label 
                        key='label'
                        className={styles.checkboxInput} 
                        style={{border: '1px solid #ccc', backgroundColor: '#fbfbfb'}}
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    />
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {(edit?.id === field?.id) && (
                    <motion.div
                        key='text'
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    >
                        <motion.input 
                            type='text' 
                            maxLength='50' 
                            value={option} 
                            onChange={e => setOption(e.target.value)} 
                            placeholder='Add Option' 
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={(edit?.id === field?.id) ? 'titleEdit' : 'titleNormal'}
                        />
                        <button className={styles.optionBtn} onClick={handleAddOption} disabled={!option}><i className='far fa-plus' /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        </>
    );
};

// checkbox
export const CheckBox = ({field, fields, config, edit, qrTypeParent}) => {

    const [ checked, setChecked ] = useState(false);

    return (
        <div className={styles.checkboxInputWrap}>
            <motion.label
                key={field.id}
                className={styles.checkboxInput}
                transition={transition}
                variants={variants}
                initial={false}
                animate={checked ? 'checked' : 'notChecked'}
                style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
            >
                <input type='checkbox' value={checked} onChange={e => setChecked(e.target.checked)} />
                <AnimatePresence initial={false}>
                    {checked && (
                        <motion.i
                            transition={transition}
                            variants={variants}
                            initial='hide'
                            animate='show'
                            exit='hide'
                            className='fas fa-check'
                        />
                    )}
                </AnimatePresence>
            </motion.label>
            <FieldName field={field} fields={fields} config={config} edit={edit} qrTypeParent={qrTypeParent} />
        </div>
    )
};

// dropdown
export const Dropdown = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();
    const [ option, setOption ] = useState('');

    const handleAddOption = () => {
        if (option) {
            dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: [...(field.options ?? []), option]}})
        }
        setOption('');
    };

    const handleRemoveOption = (optionToRemove) => {
        const newOptions = field.options.filter(option => option !== optionToRemove);
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
    };

    const handleEditOption = (e, optionIndex) => {
        // find and update option
        const newValue = e.target.value;
        
        if (optionIndex >= 0 && optionIndex < field.options.length) {
            const newOptions = [...field.options];
            newOptions[optionIndex] = newValue;
            
            dataDispatch({type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions}})
        }
    };

    const [ scroll, setScroll ] = useState(false);
    const handleScroll = (e) => {
        const scrollTop = e.currentTarget.scrollTop;

        if (scrollTop > 0) {
            setScroll(true);
        } else {
            setScroll(false);
        }
    };

    return (
        <>
        <div className={styles.inputGroup}>
            <select 
                className={styles.dropdownInput} 
                value='0'
                onChange={() => null}
                style={(edit?.id === field?.id) ? {cursor: 'pointer', pointerEvents: 'initial'} : {cursor: 'initial', pointerEvents: 'none'}}
            >
                <option value='0' disabled>-- Select --</option>
                {field?.options?.length > 0 && field.options.map((o,i) => (
                    <option key={i} value={o}>{o}</option>
                ))}
            </select>
        </div>
        <div className={`${styles.options} ${((edit?.id === field?.id) && field?.options?.length >= 5) ? styles.max : null} ${scroll ? styles.scrolled : null}`} onScroll={handleScroll}>
            <AnimatePresence initial={false}>
                {(edit?.id === field?.id) && (
                    field?.options?.length > 0 && field.options.map((o,i) => (
                        <motion.div
                            key={`option-${i}`}
                            className={styles.optionWrap}
                            transition={transition}
                            variants={variants}
                            initial='hide'
                            animate='show'
                            exit='hide'
                        >
                            <input type='text' style={{borderRadius: '0', backgroundColor: '#edf0f6', borderBottom: '2px solid #607ca7'}} value={o} onChange={(e) => handleEditOption(e,i)} />
                            <button className={styles.optionBtn} onClick={() => handleRemoveOption(o)}><i className='far fa-trash-can' /></button>
                        </motion.div>
                    ))
                )}
            </AnimatePresence>
            <AnimatePresence initial={false}>
                {(edit?.id === field?.id) && (
                    <motion.div
                        key='option'
                        className={styles.optionWrap}
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    >
                        <input style={{borderRadius: '0', backgroundColor: '#edf0f6', borderBottom: '2px solid #607ca7', marginTop: '.25rem'}} type='text' maxLength='50' value={option} onChange={e => setOption(e.target.value)} placeholder='Add Option' />
                        <button className={styles.optionBtn} onClick={handleAddOption} disabled={!option}><i className='far fa-plus' /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        </>
    );
};

// signature
export const Signature = () => (
    <div className={styles.signatureInput}>
        <img src='/images/john-doe.png' alt='fake signature' />
    </div>
);

// datetime-local
export const DateTime = ({field, edit}) => (
    <input 
        type='datetime-local' 
        className={styles.textInput} 
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
)

// count
export const Count = ({field, edit}) => (
    <input 
        type='number' 
        className={styles.textInput} 
        placeholder='e.g., 14' 
        disabled
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
)

// email
export const Email = ({field, edit}) => (
    <input 
        type='email' 
        className={styles.textInput} 
        placeholder='example@email.com'
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
);

// phone
export const Phone = ({field, edit}) => (
    <input 
        type='tel' 
        className={styles.textInput} 
        placeholder='(123) 456-7890'
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
);

// address
export const Address = ({field, edit}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', width: '100%', pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}` }}>
        <input 
            type='text' 
            className={styles.textInput} 
            placeholder='Street Address'
            disabled
        />
        <input 
            type='text' 
            className={styles.textInput} 
            placeholder='City / Township'
            disabled
        />
        <div style={{ display: 'flex', gap: '.5rem' }}>
            <input 
                type='text' 
                className={styles.textInput} 
                placeholder='State / Province'
                disabled
                style={{ flex: 1 }}
            />
            <input 
                type='text' 
                className={styles.textInput} 
                placeholder='Postal Code'
                disabled
                style={{ flex: 1 }}
            />
        </div>
    </div>
);

// text box
export const TextBox = ({field, edit}) => (
    <input 
        type='text' 
        className={styles.textInput} 
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
);

export const TextArea = ({field, edit}) => (
    <textarea 
        className={styles.textAreaInput} 
        style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
    />
);

// Opinion Scale (Number rating)
export const OpinionScale = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();

    const handleScale = (operation) => {
        let newValue = field?.scale || 5;
        switch(operation) {
            case 'add':
                newValue++;
                break;
            case 'subtract':
                newValue--
                break;
            default:
                break;
        }
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'scale', value: newValue}})
    };

    return (
        <motion.div 
            className={styles.numberContainer}
            transition={transition}
            variants={variants}
            initial={false}
            animate={(edit?.id === field?.id) ? 'numberEdit' : 'numberNormal'}
        >
            {NumberPreview(field?.scale || 5)}
            <AnimatePresence mode='wait' initial={false}>
                {(edit?.id === field?.id) && (
                    <motion.div
                        key='number-btns'
                        className={styles.numberBtnWrap}
                        transition={transition}
                        variants={variants}
                        initial='hide'
                        animate='show'
                        exit='hide'
                    >
                        <button className={styles.numberBtn} onClick={() => handleScale('subtract')} disabled={field?.scale === 1}><i className='far fa-minus' /></button>
                        <button className={styles.numberBtn} onClick={() => handleScale('add')} disabled={field?.scale === 10}><i className='far fa-plus' /></button>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// Icon Rating
export const IconRating = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();
    const [ ref, open, setOpen ] = useClickOutside(false);

    const handleIconChange = (iconValue) => {
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'icon', value: iconValue }})
    };

    return (
        <>
        <motion.div
            key='edit'
            className={styles.customSelect}
            transition={transition}
            variants={variants}
            initial={false}
            animate={(edit?.id === field?.id) ? 'iconEdit' : 'iconNormal'}
            ref={ref}
            onClick={(edit?.id === field?.id) ? () => setOpen(!open) : null}
            style={{pointerEvents: `${(edit?.id === field?.id) ? 'initial' : 'none'}`}}
        >
            {IconPreview(field?.icon)}
            {(edit?.id === field?.id) && <i className='far fa-caret-right' />}
            <AnimatePresence mode='wait' initial={false}>
                {open && (
                    <motion.div
                        className={styles.selectMenu}
                        transition={transition}
                        variants={variants}
                        initial='hideMenu'
                        animate='showMenu'
                        exit='hideMenu'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.selectMenuItem} onClick={() => handleIconChange('stars')}>{IconPreview('stars')}</div>
                        <div className={styles.selectMenuItem} onClick={() => handleIconChange('hearts')}>{IconPreview('hearts')}</div>
                        <div className={styles.selectMenuItem} onClick={() => handleIconChange('thumbs-up')}>{IconPreview('thumbs-up')}</div>
                        <div className={styles.selectMenuItem} onClick={() => handleIconChange('flames')}>{IconPreview('flames')}</div>
                        <div className={styles.selectMenuItem} onClick={() => handleIconChange('emotions')}>{IconPreview('emotions')}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
        </>
    );
};


// required toggle
export const RequiredToggle = ({field, edit, qrTypeParent}) => {

    const { dataDispatch } = useAuth();

    const handleRequired = (e) => {
        const checked = e.target.checked;
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'required', value: checked }})
    };

    return (
        <AnimatePresence mode='wait' initial={false}>
            {(edit?.id === field?.id) && (
                <motion.div 
                    key='required'
                    className={styles.requiredWrap}
                    transition={transition}
                    variants={variants}
                    initial='hide'
                    animate='show'
                    exit='hide'
                >
                    <motion.label 
                        className={styles.toggleWrap}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={field?.required ? 'toggleWrapOn' : 'toggleWrapOff'}
                    >
                        <motion.span 
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={field.required ? 'toggleOn' : 'toggleOff'}
                        />
                        <input id={`required-checkbox-${field.id}`} type='checkbox' checked={field?.required || false} onChange={handleRequired} />
                    </motion.label>
                    <div style={{color: `${field?.required ? '#607ca7' : '#666'}`, transition: 'color .2s ease'}}>Required</div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// archive button
export const ArchiveButton = ({ field, edit, onArchive, onDelete, originalFields }) => {
    const isActive = edit?.id === field?.id;
    const isArchived = !!field.archived;
    // Show 'Delete' if field doesn't exist in originalFields or when field is archived
    const fieldExistsInOriginal = originalFields?.some(f => f.id === field.id);
    const showDelete = !fieldExistsInOriginal || isArchived;
    const title = showDelete ? 'Delete' : 'Archive';
    const handleClick = (e) => {
        if (!isActive) return;
        if (showDelete) {
            onDelete?.(e);
        } else {
            onArchive?.(e);
        }
    };

    return (
        <button
            className={`${styles.archiveBtn} ${showDelete ? styles.deleteState : styles.archiveState}`}
            title={title}
            onClick={handleClick}
            style={{ opacity: isActive ? 1 : 0, pointerEvents: isActive ? 'initial' : 'none' }}
        >
            <i className={`far fa-${showDelete ? 'trash-can' : 'archive'}`} />
        </button>
    );
};

export const RestoreButton = ({ field, edit, qrTypeParent, onRestore }) => {
    const isActive = edit?.id === field?.id;
    if (!field.archived) return null;
    return (
        <AnimatePresence mode='wait' initial={false}>
            {isActive && (
                <motion.div
                    key='restore'
                    className={styles.restoreWrap}
                    transition={transition}
                    variants={variants}
                    initial='hide'
                    animate='show'
                    exit='hide'
                >
                    <button
                        className={styles.restoreBtn}
                        type='button'
                        onClick={onRestore}
                        title='Restore'
                    >
                        <i className='far fa-rotate-left' />
                        <span>Restore</span>
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// text box read only
export const Header = ({field, edit, qrTypeParent}) => {
    const { dataDispatch, config } = useAuth();

    const fieldDefinition = config?.field_types?.fields?.[field?.type] || {};
    const isHeaderType = fieldDefinition?.type === 'header';

    const handleChange = (e) => {
        const value = e.target.value;
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'name', value } });
    };

    // apply styles from field.styles
    const appliedStyles = {
        ...(field.styles || {}),
        fontSize: field.styles?.fontSize ?? (isHeaderType ? 32 : undefined),
        fontWeight: field.styles?.fontWeight ?? (isHeaderType ? '700' : undefined),
        textAlign: field.styles?.textAlign ?? (isHeaderType ? 'center' : undefined),
        pointerEvents: (edit?.id === field?.id && !field.archived) ? 'initial' : 'none',
        minHeight: 'auto'
    };

    return (
        <AnimatePresence mode='wait' initial={false}>
            <div style={{ width: '100%' }}>
                <FormatToolbar field={field} edit={edit} qrTypeParent={qrTypeParent} />
                <motion.input
                    key={field.id}
                    type='text'
                    className={styles.headerText}
                    value={field.name}
                    onChange={handleChange}
                    placeholder={'Enter heading text'}
                    required
                    disabled={!edit || field.archived}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                    style={appliedStyles}
                />
            </div>
        </AnimatePresence>
    );
};

export const Paragraph = ({field, edit, qrTypeParent}) => {
    const { dataDispatch } = useAuth();

    const handleChange = (e) => {
        const value = e.target.value;
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'name', value } });
    };

    const appliedStyles = {
        ...(field.styles || {}),
        pointerEvents: (edit?.id === field?.id && !field.archived) ? 'initial' : 'none',
        minHeight: '60px',
        resize: 'vertical',
        textAlign: (field.styles && field.styles.textAlign) || 'center'
    };

    return (
        <AnimatePresence mode='wait' initial={false}>
            <div style={{ width: '100%' }}>
                <FormatToolbar field={field} edit={edit} qrTypeParent={qrTypeParent} />
                <motion.textarea
                    key={field.id}
                    className={styles.fieldName}
                    value={field.name}
                    onChange={handleChange}
                    placeholder={'Enter text to display to users'}
                    required
                    disabled={!edit || field.archived}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                    style={appliedStyles}
                />
            </div>
        </AnimatePresence>
    );
};

export const HeaderParagraph = ({field, edit, qrTypeParent}) => {
    const { dataDispatch } = useAuth();
    const isEditing = edit?.id === field?.id && !field.archived;
    const nameValue = field?.name && typeof field.name === 'object'
        ? field.name
        : { header: '', paragraph: '' };
    const styleGroups = field?.styles && typeof field.styles === 'object'
        ? field.styles
        : {};
    const sharedStyles = Object.fromEntries(
        Object.entries(styleGroups).filter(([key]) => !['header', 'paragraph'].includes(key))
    );
    const headerDefaults = {
        fontSize: 32,
        fontWeight: '700',
        textAlign: 'center',
        color: '#000000'
    };
    const paragraphDefaults = {
        fontSize: 16,
        textAlign: 'center',
        color: '#000000'
    };
    const headerStylesState = {
        ...sharedStyles,
        ...(styleGroups.header || {})
    };
    const paragraphStylesState = {
        ...sharedStyles,
        ...(styleGroups.paragraph || {})
    };

    const handleTextChange = (section) => (e) => {
        const value = e.target.value;
        dataDispatch({
            type: qrTypeParent,
            data: {
                id: field.id,
                field: 'name',
                value: {
                    ...nameValue,
                    [section]: value
                }
            }
        });
    };

    const updateSectionStyles = (section, partial) => {
        dataDispatch({
            type: qrTypeParent,
            data: {
                id: field.id,
                field: 'styles',
                value: {
                    ...styleGroups,
                    [section]: {
                        ...(styleGroups[section] || {}),
                        ...partial
                    }
                }
            }
        });
    };

    const appliedHeaderStyles = {
        ...headerDefaults,
        ...headerStylesState,
        pointerEvents: isEditing ? 'initial' : 'none',
        minHeight: 'auto'
    };

    const appliedParagraphStyles = {
        ...paragraphDefaults,
        ...paragraphStylesState,
        pointerEvents: isEditing ? 'initial' : 'none',
        minHeight: isEditing ? '60px' : 'auto',
        resize: isEditing ? 'vertical' : 'none',
        overflow: 'hidden'
    };

    return (
        <AnimatePresence mode='wait' initial={false}>
            <div className={`${styles.headerParagraphGroup} ${isEditing ? styles.editing : ''}`}>
                <div className={styles.headerParagraphSection}>
                    <SectionFormatToolbar
                        title='Header'
                        isVisible={isEditing}
                        stylesState={headerStylesState}
                        fieldDefaults={headerDefaults}
                        onUpdate={(partial) => updateSectionStyles('header', partial)}
                    />
                    <motion.input
                        key={`${field.id}-header`}
                        type='text'
                        className={styles.headerText}
                        value={nameValue.header}
                        onChange={handleTextChange('header')}
                        placeholder='Enter heading text'
                        required
                        disabled={!edit || field.archived}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                        style={appliedHeaderStyles}
                    />
                </div>
                <div className={styles.headerParagraphSection}>
                    <SectionFormatToolbar
                        title='Paragraph'
                        isVisible={isEditing}
                        stylesState={paragraphStylesState}
                        fieldDefaults={paragraphDefaults}
                        onUpdate={(partial) => updateSectionStyles('paragraph', partial)}
                    />
                    <motion.textarea
                        key={`${field.id}-paragraph`}
                        className={styles.fieldName}
                        value={nameValue.paragraph}
                        onChange={handleTextChange('paragraph')}
                        placeholder='Enter text to display to users'
                        required
                        disabled={!edit || field.archived}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                        style={appliedParagraphStyles}
                    />
                </div>
            </div>
        </AnimatePresence>
    );
};

export const CarouselField = ({ field, edit, qrTypeParent }) => {
    const acceptableFileTypes = useMemo(() => ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'], []);
    const fileHelpers = useFileUpload({
        type: qrTypeParent,
        acceptableFileTypes,
        fileHandleType: 'qr_files',
    });
    const { handleFileSelected, filesToDisplay, error } = fileHelpers;
    const isEditing = edit?.id === field?.id && !field.archived;
    const uploadDisabled = filesToDisplay.length >= 5;

    return (
        <div className={styles.carouselField}>
            <div className={styles.carouselPreviewWrap}>
                <TrackerCarousel files={filesToDisplay} />
            </div>
            {isEditing && (
                <div className={styles.carouselEditor}>
                    <div className={styles.carouselUploadWrap}>
                        <FileInput
                            onFileSelect={handleFileSelected}
                            acceptedFileTypes={'.jpg,.jpeg,.png,.webp'}
                            multiple={true}
                            label={uploadDisabled ? 'Max 5 Images' : 'Upload Images'}
                            id={`carousel-field-${field.id}`}
                            name={`carousel-field-${field.id}`}
                            disabled={uploadDisabled}
                        />
                    </div>
                    {error && <div className={styles.fileError}>{error}</div>}
                    {filesToDisplay.length > 0 && <FilesList fileHelpers={fileHelpers} />}
                </div>
            )}
        </div>
    );
};

export const Divider = (_) => {
    return (
        <div style={{ width: '100%', padding: '0.5rem 0' }}>
            <hr style={{ 
                width: '100%', 
                border: 'none', 
                borderTop: '1px solid transparent',
                borderImage: 'linear-gradient(to right, transparent, #666 20%, #666 80%, transparent)',
                borderImageSlice: 1,
                margin: 0 
            }} />
        </div>
    );
};

export const Banner = ({field, edit, qrTypeParent}) => {
    const { dataDispatch } = useAuth();
    const showIcon = field.options && field.options.includes('show-icon');
    const bannerColor = field.styles?.color || '#607ca7';
    const bannerBackground = hexToRgba(bannerColor, 0.12);

    const handleChange = (e) => {
        const value = e.target.value;
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'name', value } });
    };

    const handleToggleIcon = () => {
        const newOptions = showIcon 
            ? field.options.filter(opt => opt !== 'show-icon')
            : [...(field.options || []), 'show-icon'];
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'options', value: newOptions } });
    };

    const appliedStyles = {
        ...(field.styles || {}),
        flex: 1,
        minHeight: '60px',
        resize: 'vertical',
        backgroundColor: 'transparent',
        color: bannerColor,
        pointerEvents: (edit?.id === field?.id && !field.archived) ? 'initial' : 'none'
    };

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <div style={{
                width: '100%',
                textAlign: 'left',
                backgroundColor: bannerBackground,
                border: `2px solid ${bannerColor}`,
                color: bannerColor,
                borderRadius: '.35rem',
                padding: '.5rem',
                fontSize: '.85rem',
                lineHeight: '1.25rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '.5rem'
            }}>
                {showIcon && <i className='far fa-lightbulb-on' style={{ marginTop: '.1rem', flexShrink: 0, color: bannerColor }} />}
                <div style={{ flex: 1 }}>
                    <FormatToolbar field={field} edit={edit} qrTypeParent={qrTypeParent} />
                    <AnimatePresence mode='wait' initial={false}>
                        <motion.textarea
                            key={field.id}
                            className={styles.bannerTextArea}
                            value={field.name}
                            onChange={handleChange}
                            placeholder={'Enter banner text'}
                            required
                            readOnly={!edit || field.archived}
                            transition={transition}
                            variants={variants}
                            initial={false}
                            animate={(edit?.id === field?.id && field?.type !== 'date') ? 'titleEdit' : 'titleNormal'}
                            custom={{ backgroundColor: 'transparent', borderBottom: `2px solid ${bannerColor}` }}
                            style={appliedStyles}
                        />
                    </AnimatePresence>
                </div>
            </div>
            {edit?.id === field?.id && !field.archived && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.9rem' }}>
                    <motion.label 
                        className={styles.checkboxInput}
                        transition={transition}
                        variants={variants}
                        initial={false}
                        animate={showIcon ? 'checked' : 'notChecked'}
                    >
                        <input 
                            type='checkbox' 
                            checked={showIcon} 
                            onChange={handleToggleIcon}
                        />
                        <AnimatePresence initial={false}>
                            {showIcon && (
                                <motion.i
                                    transition={transition}
                                    variants={variants}
                                    initial='hide'
                                    animate='show'
                                    exit='hide'
                                    className='fas fa-check'
                                />
                            )}
                        </AnimatePresence>
                    </motion.label>
                    <span style={{ cursor: 'pointer' }} onClick={handleToggleIcon}>Show lightbulb icon</span>
                </div>
            )}
        </div>
    );
};

export const SlidingScale = ({field, edit, qrTypeParent}) => {
    const { dataDispatch } = useAuth();
    const value = field.scale ?? 50;

    const handleChange = (e) => {
        const newValue = parseInt(e.target.value);
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'scale', value: newValue } });
    };

    return (
        <div className={styles.slidingScaleRow}>
            <AnimatePresence mode='wait' initial={false}>
                <motion.input
                    key={field.id}
                    type='range'
                    min='0'
                    max='100'
                    value={value}
                    onChange={handleChange}
                    disabled={!edit || field.archived}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={(edit?.id === field?.id) ? 'titleEdit' : 'titleNormal'}
                    className={styles.slidingScaleInput}
                    style={{
                        pointerEvents: (edit?.id === field?.id && !field.archived) ? 'initial' : 'none',
                        cursor: (edit?.id === field?.id && !field.archived) ? 'pointer' : 'default'
                    }}
                />
            </AnimatePresence>
            <div className={styles.scaleValue}>{value}</div>
        </div>
    );
};

const IconPreview = (icon) => {
    switch (icon) {
        case 'hearts':
            return (
                <div className={styles.iconPreview}>
                    <i className='fas fa-heart' style={{ color: 'red' }} />
                    <i className='fas fa-heart' style={{ color: 'red' }} />
                    <i className='fas fa-heart' style={{ color: 'red' }} />
                    <i className='far fa-heart' style={{ color: 'red' }} />
                    <i className='far fa-heart' style={{ color: 'red' }} />
                </div>
            );
        case 'thumbs-up':
            return (
                <div className={styles.iconPreview}>
                    <i className='fas fa-thumbs-up' style={{ color: '#607ca7' }} />
                    <i className='fas fa-thumbs-up' style={{ color: '#607ca7' }} />
                    <i className='fas fa-thumbs-up' style={{ color: '#607ca7' }} />
                    <i className='far fa-thumbs-up' style={{ color: '#607ca7' }} />
                    <i className='far fa-thumbs-up' style={{ color: '#607ca7' }} />
                </div>
            );
        case 'flames':
            return (
                <div className={styles.iconPreview}>
                    <i className='fa-duotone fa-fire' style={{ "--fa-primary-color": "#e14714", "--fa-secondary-color": "#ff9b29", '--fa-secondary-opacity': '1' }} />
                    <i className='fa-duotone fa-fire' style={{ '--fa-primary-color': '#e14714', '--fa-secondary-color': '#ff9b29', '--fa-secondary-opacity': '1' }} />
                    <i className='fa-duotone fa-fire' style={{ '--fa-primary-color': '#e14714', '--fa-secondary-color': '#ff9b29', '--fa-secondary-opacity': '1' }} />
                    <i className='far fa-fire' style={{ color: '#161d2d' }} />
                    <i className='far fa-fire' style={{ color: '#161d2d' }} />
                </div>
            );
        case 'emotions':
            return (
                <div className={styles.iconPreview}>
                    <i className='far fa-face-angry' style={{ color: 'red' }} />
                    <i className='far fa-face-frown' style={{ color: 'orangered' }} />
                    <i className='far fa-face-grin' style={{ color: 'purple' }} />
                    <i className='far fa-face-grin-wide' style={{ color: 'blue' }} />
                    <i className='far fa-face-laugh-beam' style={{ color: 'green' }} />
                </div>
            );
        case 'stars':
        default:
            return (
                <div className={styles.iconPreview}>
                    <i className='fas fa-star' style={{ color: 'gold' }} />
                    <i className='fas fa-star' style={{ color: 'gold' }} />
                    <i className='fas fa-star' style={{ color: 'gold' }} />
                    <i className='far fa-star' style={{ color: 'gold' }} />
                    <i className='far fa-star' style={{ color: 'gold' }} />
                </div>
            );
    };
};

// helper
const NumberPreview = (scale) => {
    let numbs = [];
    for (let i = 1; i <= scale; i++) {
        numbs.push(i);
    };

    return (
        <>
            <div className={styles.numberPreview}>
                {numbs.filter(n => n !== 10).map((n, i) => (
                    <div key={i} className={styles.number}>
                        <i className={`far fa-${n}`} />
                    </div>
                ))}
                {numbs.filter(n => n === 10).map((n, i) => (
                    <div key={i} className={styles.number}>
                        <i className='far fa-1' />
                        <i className='far fa-0' />
                    </div>
                ))}
            </div>
        </>
    );
};

// new: formatting toolbar for text fields
const TextFormatToolbar = ({ stylesState = {}, fieldDefaults = {}, onUpdate }) => {
    const defaultFontSize = stylesState.fontSize ?? (fieldDefaults.fontSize || 16);
    const defaultBold = fieldDefaults.fontWeight || '400';
    const defaultAlign = fieldDefaults.textAlign || 'center';
    const defaultColor = fieldDefaults.color || '#000000';
    const isBold = (stylesState.fontWeight ?? defaultBold) === '700';
    const isItalic = stylesState.fontStyle === 'italic';
    const isUnderline = stylesState.textDecoration === 'underline';
    const isStrikethrough = stylesState.textDecoration === 'line-through';
    const currentAlign = stylesState.textAlign || defaultAlign;
    const currentColor = stylesState.color || defaultColor;

    return (
        <div className={styles.formatToolbar}>
            <label className={styles.formatLabel}>
                Size
                <input
                    type='number'
                    min='10'
                    max='72'
                    value={defaultFontSize}
                    onChange={e => onUpdate({ fontSize: Number(e.target.value) })}
                    className={styles.formatSizeInput}
                />
            </label>

            <label className={styles.formatLabel}>
                Color
                <input
                    type='color'
                    value={currentColor}
                    onChange={e => onUpdate({ color: e.target.value })}
                    className={styles.formatColorInput}
                />
            </label>

            <div className={styles.formatAlignGroup}>
                <button
                    type='button'
                    onClick={() => onUpdate({ textAlign: 'left' })}
                    title='Align Left'
                    className={`${styles.formatButton} ${currentAlign === 'left' ? styles.active : styles.inactive}`}
                >
                    <i className='far fa-align-left' />
                </button>

                <button
                    type='button'
                    onClick={() => onUpdate({ textAlign: 'center' })}
                    title='Align Center'
                    className={`${styles.formatButton} ${currentAlign === 'center' ? styles.active : styles.inactive}`}
                >
                    <i className='far fa-align-center' />
                </button>

                <button
                    type='button'
                    onClick={() => onUpdate({ textAlign: 'right' })}
                    title='Align Right'
                    className={`${styles.formatButton} ${currentAlign === 'right' ? styles.active : styles.inactive}`}
                >
                    <i className='far fa-align-right' />
                </button>
            </div>

            <button
                type='button'
                onClick={() => onUpdate({ fontWeight: isBold ? '400' : '700' })}
                title='Bold'
                className={`${styles.formatButton} ${isBold ? styles.active : styles.inactive}`}
            >
                <strong>B</strong>
            </button>

            <button
                type='button'
                onClick={() => onUpdate({ fontStyle: isItalic ? 'normal' : 'italic' })}
                title='Italic'
                className={`${styles.formatButton} ${isItalic ? styles.active : styles.inactive}`}
            >
                <i style={{ fontStyle: 'italic' }}>I</i>
            </button>

            <button
                type='button'
                onClick={() => onUpdate({ textDecoration: isUnderline ? 'none' : 'underline' })}
                title='Underline'
                className={`${styles.formatButton} ${isUnderline ? styles.active : styles.inactive}`}
            >
                <u>U</u>
            </button>

            <button
                type='button'
                onClick={() => onUpdate({ textDecoration: isStrikethrough ? 'none' : 'line-through' })}
                title='Strikethrough'
                className={`${styles.formatButton} ${isStrikethrough ? styles.active : styles.inactive}`}
            >
                <s>S</s>
            </button>
        </div>
    );
};

const SectionFormatToolbar = ({ title, isVisible, stylesState, fieldDefaults, onUpdate }) => {
    if (!isVisible) return null;

    return (
        <div className={styles.sectionToolbar}>
            <span className={styles.sectionToolbarLabel}>{title}</span>
            <TextFormatToolbar
                stylesState={stylesState}
                fieldDefaults={fieldDefaults}
                onUpdate={onUpdate}
            />
        </div>
    );
};

const FormatToolbar = ({ field, edit, qrTypeParent }) => {
    const { dataDispatch, config } = useAuth();
    const stylesState = field.styles || {};
    const fieldDefinition = config?.field_types?.fields?.[field?.type] || {};
    const fieldDefaults = fieldDefinition?.defaultStyles || {};

    const update = (partial) => {
        const newStyles = { ...(field.styles || {}), ...partial };
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'styles', value: newStyles }});
    };

    if (!(edit?.id === field?.id) || field?.archived) return null;

    return (
        <TextFormatToolbar
            stylesState={stylesState}
            fieldDefaults={fieldDefaults}
            onUpdate={update}
        />
    );
};
